import React, { useState } from 'react';

/**
 * ReviewRulesSection
 *
 * Manages the review_rules settings block:
 *   high_value_review_amount  (float) — orders >= this amount always route to human review,
 *                                        regardless of confidence score. 0 disables the rule.
 *   always_review_customer_ids (list[str]) — block-list of customer IDs that never auto-book.
 *
 * Follows the existing Section pattern: receives `value` (the review_rules sub-block) and
 * calls `onChange` with the updated sub-block.
 */
export default function ReviewRulesSection({ value = {}, onChange, readOnly }) {
  const alwaysReviewIds = value.always_review_customer_ids ?? [];
  const [chipInput, setChipInput] = useState('');

  function patch(changes) {
    onChange({ ...value, ...changes });
  }

  // ── Chip list helpers ──────────────────────────────────────────────────────

  function addId(raw) {
    // Accept comma- or newline-separated pastes, trim whitespace, skip dupes + empties
    const incoming = raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s && !alwaysReviewIds.includes(s));
    if (!incoming.length) return;
    patch({ always_review_customer_ids: [...alwaysReviewIds, ...incoming] });
    setChipInput('');
  }

  function removeId(id) {
    patch({ always_review_customer_ids: alwaysReviewIds.filter((x) => x !== id) });
  }

  function handleChipKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (chipInput.trim()) addId(chipInput);
    } else if (e.key === 'Backspace' && !chipInput && alwaysReviewIds.length > 0) {
      removeId(alwaysReviewIds[alwaysReviewIds.length - 1]);
    }
  }

  function handleChipPaste(e) {
    // Let browser paste the text, then parse it via onChange below
    // We handle it in the onChange so the state settles before parsing
    const text = e.clipboardData.getData('text');
    if (text.includes(',') || text.includes('\n')) {
      e.preventDefault();
      addId(chipInput + text);
    }
  }

  return (
    <div className="space-y-6">
      {/* ── High-value review threshold ── */}
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
          High-Value Order Review
        </div>
        <p className="text-xs text-boss-text-muted">
          Orders at or above this dollar amount are always routed to human review, regardless of
          confidence score. Set to{' '}
          <code className="font-mono bg-boss-bg-elevated px-1 rounded">0</code> to disable.
        </p>
        <div className="flex items-center gap-2 w-44">
          <span className="text-sm text-boss-text-secondary shrink-0">$</span>
          <input
            id="review-high-value-amount"
            type="number"
            min="0"
            step="100"
            className="input-field w-full text-sm"
            placeholder="5000"
            value={value.high_value_review_amount ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              patch({ high_value_review_amount: raw === '' ? 0 : parseFloat(raw) || 0 });
            }}
            disabled={readOnly}
            aria-label="High-value review threshold in dollars"
          />
        </div>
        {(value.high_value_review_amount ?? 0) > 0 && (
          <p className="text-xs text-boss-text-muted">
            Orders worth{' '}
            <strong className="text-boss-text">
              ${Number(value.high_value_review_amount).toLocaleString()}
            </strong>{' '}
            or more will always require a reviewer, regardless of match confidence.
          </p>
        )}
        {(value.high_value_review_amount ?? 0) === 0 && (
          <p className="text-xs text-boss-text-muted">
            High-value rule is disabled — orders of any amount can auto-book when confidence
            is sufficient.
          </p>
        )}
      </div>

      {/* ── Always-review customer block-list ── */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
          Always-Review Customer IDs
        </div>
        <p className="text-xs text-boss-text-muted">
          Orders for these AppDirect customer IDs are always routed to human review — they never
          auto-book, regardless of confidence or amount. Paste comma-separated or one per line.
        </p>

        {/* Chip display */}
        {alwaysReviewIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {alwaysReviewIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full border border-boss-border bg-boss-bg-elevated px-2.5 py-0.5 text-xs text-boss-text font-mono"
              >
                {id}
                {!readOnly && (
                  <button
                    type="button"
                    className="text-boss-text-muted hover:text-boss-status-danger transition-colors ml-0.5"
                    onClick={() => removeId(id)}
                    aria-label={`Remove customer ID ${id}`}
                  >
                    &times;
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {!readOnly && (
          <div className="flex gap-2 items-start">
            <input
              type="text"
              className="input-field text-sm font-mono flex-1 sm:flex-none sm:w-64"
              placeholder="customer-id or paste multiple"
              value={chipInput}
              onChange={(e) => setChipInput(e.target.value)}
              onKeyDown={handleChipKeyDown}
              onPaste={handleChipPaste}
              disabled={readOnly}
              aria-label="Add customer ID to always-review list"
            />
            <button
              type="button"
              className="btn-secondary text-xs px-3 py-2 shrink-0"
              onClick={() => { if (chipInput.trim()) addId(chipInput); }}
              disabled={!chipInput.trim()}
            >
              Add
            </button>
          </div>
        )}

        {alwaysReviewIds.length === 0 && (
          <div className="glass-card rounded-lg border border-boss-border p-3 text-xs text-boss-text-muted">
            No customers on the always-review list. All eligible orders will be handled by the
            confidence threshold rules.
          </div>
        )}
      </div>
    </div>
  );
}
